// Jauge d'alerte Franchise TVA

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  useTheme,
  Button,
} from '@mui/material';
import { Warning, CheckCircle, Store, TrendingUp } from '@mui/icons-material';
import { Alertes } from '../../types/dashboard';
import { useNavigate } from 'react-router-dom';

interface JaugeItemProps {
  titre: string;
  valeurActuelle: number;
  seuil: number;
  pourcentage: number;
  icon: React.ReactNode;
  description?: string;
}

const JaugeItem: React.FC<JaugeItemProps> = ({
  titre,
  valeurActuelle,
  seuil,
  pourcentage,
  icon,
  description,
}) => {
  const theme = useTheme();

  const getCouleurJauge = () => {
    if (pourcentage >= 90) return theme.palette.error.main;
    if (pourcentage >= 80) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const isAlerte = pourcentage >= 80;

  const formatMontant = (montant: number) => {
    return montant.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              backgroundColor: `${getCouleurJauge()}20`,
              borderRadius: 1,
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {titre}
          </Typography>
          {isAlerte ? (
            <Warning fontSize="small" sx={{ color: theme.palette.warning.main }} />
          ) : (
            <CheckCircle fontSize="small" sx={{ color: theme.palette.success.main }} />
          )}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: getCouleurJauge() }}>
          {pourcentage.toFixed(1)}%
        </Typography>
      </Box>

      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {description}
        </Typography>
      )}

      <LinearProgress
        variant="determinate"
        value={Math.min(pourcentage, 100)}
        sx={{
          height: 10,
          borderRadius: 5,
          backgroundColor: `${getCouleurJauge()}20`,
          '& .MuiLinearProgress-bar': {
            backgroundColor: getCouleurJauge(),
            borderRadius: 5,
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatMontant(valeurActuelle)} € / {formatMontant(seuil)} €
        </Typography>
        <Typography variant="caption" sx={{ color: getCouleurJauge(), fontWeight: 600 }}>
          {isAlerte ? 'Alerte seuil' : 'OK'}
        </Typography>
      </Box>
    </Box>
  );
};

interface AlertJaugesProps {
  alertes: Alertes;
}

export const AlertJauges: React.FC<AlertJaugesProps> = ({ alertes }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const isAlerteGlobale = alertes.progressionSeuilFranchise >= 80;

  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Seuil de Franchise TVA
        </Typography>

        {/* Jauge Franchise en Base - SEULE jauge conservée */}
        <JaugeItem
          titre="Franchise en Base TVA"
          valeurActuelle={alertes.caAnnuel}
          seuil={alertes.seuilFranchise}
          pourcentage={alertes.progressionSeuilFranchise}
          icon={<Store sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
          description="Au-delà de 36 800€, vous devrez facturer la TVA à vos clients"
        />

        {/* Message d'alerte si > 80% */}
        {isAlerteGlobale && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: `${theme.palette.warning.main}15`,
              border: `1px solid ${theme.palette.warning.main}40`,
              display: 'flex',
              alignItems: 'start',
              gap: 1,
            }}
          >
            <Warning fontSize="small" sx={{ color: theme.palette.warning.main, mt: 0.2 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                ⚠️ Attention : Seuil proche
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Au-delà du seuil, vous devrez facturer la TVA (20%) sur toutes vos prestations.
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                💡 Conseil : Consultez votre expert-comptable pour anticiper ce changement.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Informations complémentaires */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: `${theme.palette.info.main}08`,
            border: `1px solid ${theme.palette.info.main}20`,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            📊 <strong>Chiffre d'affaires annuel :</strong> {alertes.caAnnuel.toLocaleString('fr-FR')} €
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            🎯 <strong>Marge disponible :</strong> {(alertes.seuilFranchise - alertes.caAnnuel).toLocaleString('fr-FR')} € avant le seuil
          </Typography>
        </Box>

        {/* Bouton vers la page comptabilité */}
        <Button
          variant="outlined"
          size="medium"
          fullWidth
          startIcon={<TrendingUp />}
          onClick={() => navigate('/expenses')}
          sx={{
            mt: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              backgroundColor: `${theme.palette.primary.main}08`,
            },
          }}
        >
          Voir la comptabilité détaillée
        </Button>
      </CardContent>
    </Card>
  );
};
